import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Check, Copy, Loader2, Inbox, Lock, Users, Settings, Plus } from 'lucide-react';
import { listEmailAddresses, getUserEAEmails, getEAEmailSuggestions, allocateEAEmails } from '../../services/emailAddressService';
import { getPrimaryEmailDomain } from '../../services/emailDomainService';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useParadigm } from '../../contexts/ParadigmContext';

/**
 * Email Settings page for viewing inbound email addresses
 * Shows the general inbox and all configured email addresses
 */
const EmailSettings = () => {
  const { user } = useAuth();
  const { labels } = useParadigm();
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // EA Personal Email state
  const [eaEmails, setEaEmails] = useState({ standard_email: null, confidential_email: null });
  const [eaEmailsLoading, setEaEmailsLoading] = useState(true);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [allocationOptions, setAllocationOptions] = useState({
    allocateStandard: true,
    allocateConfidential: true,
    standardPrefix: '',
    confidentialPrefix: ''
  });
  const [emailDomain, setEmailDomain] = useState('veyage.io');

  // Check if general inbox exists
  const generalInbox = emailAddresses.find(e => e.email_type === 'general');

  // Fetch primary email domain
  useEffect(() => {
    getPrimaryEmailDomain()
      .then(domain => setEmailDomain(domain))
      .catch(() => {}); // Keep default on error
  }, []);

  const loadEmailAddresses = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await listEmailAddresses();
      setEmailAddresses(result.inbound_email_addresses || result.email_addresses || []);
    } catch (err) {
      console.error('Error loading email addresses:', err);
      setError('Failed to load email addresses');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEAEmails = useCallback(async () => {
    if (!user?.id) return;
    try {
      setEaEmailsLoading(true);
      const result = await getUserEAEmails(user.id);
      setEaEmails({
        standard_email: result.standard_email,
        confidential_email: result.confidential_email
      });
    } catch (err) {
      console.error('Error loading EA emails:', err);
    } finally {
      setEaEmailsLoading(false);
    }
  }, [user?.id]);

  const loadSuggestions = useCallback(async () => {
    if (!user?.id) return;
    try {
      const result = await getEAEmailSuggestions(user.id);
      setSuggestions(result);
      setAllocationOptions(prev => ({
        ...prev,
        standardPrefix: result.standard?.prefix || '',
        confidentialPrefix: result.confidential?.prefix || ''
      }));
    } catch (err) {
      console.error('Error loading suggestions:', err);
    }
  }, [user?.id]);

  const handleAllocateEmails = async () => {
    if (!user?.id) return;
    try {
      setAllocating(true);
      await allocateEAEmails(user.id, {
        allocateStandard: allocationOptions.allocateStandard,
        allocateConfidential: allocationOptions.allocateConfidential,
        standardPrefix: allocationOptions.standardPrefix,
        confidentialPrefix: allocationOptions.confidentialPrefix
      });
      await loadEAEmails();
      setShowAllocationModal(false);
    } catch (err) {
      console.error('Error allocating emails:', err);
      setError(err.response?.data?.detail || 'Failed to allocate email addresses');
    } finally {
      setAllocating(false);
    }
  };

  const handleOpenAllocationModal = async () => {
    setShowAllocationModal(true);
    await loadSuggestions();
  };

  useEffect(() => {
    loadEmailAddresses();
    loadEAEmails();
  }, [loadEmailAddresses, loadEAEmails]);

  const handleCopy = async (emailAddress, id) => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Email Settings</h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Inbound email addresses for receiving travel confirmations
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* General Inbox Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-violet-600" />
            <h2 className="text-lg font-semibold text-slate-800">General Inbox</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            A shared inbox that automatically matches senders to registered users from the organization
          </p>
        </div>

        <div className="p-6">
          {generalInbox ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800">General Inbox Active</p>
                    <code className="text-sm text-green-700 font-mono">
                      {generalInbox.email_address}
                    </code>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopy(generalInbox.email_address, generalInbox.id)}
                >
                  {copiedId === generalInbox.id ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">How it works:</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Forward travel confirmations to this address</li>
                  <li>System matches sender email to {labels.executives.toLowerCase()} in your organization</li>
                  <li>If sender is {labels.anExecutive === 'an Executive' ? 'an' : 'a'} {labels.ea}, matches passenger names to managed {labels.executives.toLowerCase()}</li>
                  <li>Unmatched emails appear in Utilities → Unfiled</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Inbox className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">General Inbox Not Configured</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Contact your administrator to set up the general inbox email address.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Personal Email Addresses Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-violet-600" />
            <h2 className="text-lg font-semibold text-slate-800">Your Personal Email Addresses</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Personal inbound email addresses allocated to you
          </p>
        </div>

        <div className="p-6">
          {eaEmailsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
            </div>
          ) : (eaEmails.standard_email || eaEmails.confidential_email) ? (
            <div className="space-y-4">
              {/* Standard Email */}
              {eaEmails.standard_email && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-blue-800">Standard Inbox</p>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                            <Users className="h-3 w-3" /> Shared with team
                          </span>
                        </div>
                        <code className="text-sm text-blue-700 font-mono">
                          {eaEmails.standard_email.email_address}
                        </code>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-600">
                        {eaEmails.standard_email.total_emails_received || 0} emails
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(eaEmails.standard_email.email_address, 'ea_standard')}
                      >
                        {copiedId === 'ea_standard' ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Visible to you, your team members, supervisors, and org admins
                  </p>
                </div>
              )}

              {/* Confidential Email */}
              {eaEmails.confidential_email && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Lock className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-amber-800">Confidential Inbox</p>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Private
                          </span>
                        </div>
                        <code className="text-sm text-amber-700 font-mono">
                          {eaEmails.confidential_email.email_address}
                        </code>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-amber-600">
                        {eaEmails.confidential_email.total_emails_received || 0} emails
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(eaEmails.confidential_email.email_address, 'ea_confidential')}
                      >
                        {copiedId === 'ea_confidential' ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    Visible only to you, your supervisors, and org admins. Team members cannot see these items.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Personal Email Addresses</h3>
              <p className="text-slate-600 max-w-md mx-auto mb-4">
                Allocate personal email addresses to receive travel confirmations directly.
              </p>
              <Button onClick={handleOpenAllocationModal}>
                <Plus className="h-4 w-4 mr-2" />
                Allocate Email Addresses
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Allocation Modal */}
      {showAllocationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-xl">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Allocate Your Email Addresses</h3>
              <p className="text-sm text-slate-600">
                We'll create personal email addresses for receiving travel confirmations.
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Standard Email Option */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="allocateStandard"
                    checked={allocationOptions.allocateStandard}
                    onChange={(e) => setAllocationOptions(prev => ({
                      ...prev,
                      allocateStandard: e.target.checked
                    }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="allocateStandard" className="font-medium text-slate-800 flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      Standard Inbox
                    </label>
                    <p className="text-sm text-slate-600 mt-1">
                      Shared with your team members, supervisors, and org admins.
                    </p>
                    {allocationOptions.allocateStandard && (
                      <div className="mt-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={allocationOptions.standardPrefix}
                            onChange={(e) => setAllocationOptions(prev => ({
                              ...prev,
                              standardPrefix: e.target.value
                            }))}
                            className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-l-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            placeholder="email prefix"
                          />
                          <span className="px-3 py-1.5 text-sm bg-slate-100 border border-l-0 border-slate-300 rounded-r-md text-slate-600">
                            @{emailDomain}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Confidential Email Option */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="allocateConfidential"
                    checked={allocationOptions.allocateConfidential}
                    onChange={(e) => setAllocationOptions(prev => ({
                      ...prev,
                      allocateConfidential: e.target.checked
                    }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="allocateConfidential" className="font-medium text-slate-800 flex items-center gap-2">
                      <Lock className="h-4 w-4 text-amber-600" />
                      Confidential Inbox
                    </label>
                    <p className="text-sm text-slate-600 mt-1">
                      Private - only visible to you, supervisors, and org admins. Team members cannot see items.
                    </p>
                    {allocationOptions.allocateConfidential && (
                      <div className="mt-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={allocationOptions.confidentialPrefix}
                            onChange={(e) => setAllocationOptions(prev => ({
                              ...prev,
                              confidentialPrefix: e.target.value
                            }))}
                            className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-l-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            placeholder="email prefix"
                          />
                          <span className="px-3 py-1.5 text-sm bg-slate-100 border border-l-0 border-slate-300 rounded-r-md text-slate-600">
                            @{emailDomain}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Both addresses use a similar prefix format for consistency. You can customize the prefixes above.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAllocationModal(false)}
                disabled={allocating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAllocateEmails}
                disabled={allocating || (!allocationOptions.allocateStandard && !allocationOptions.allocateConfidential)}
              >
                {allocating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Allocating...
                  </>
                ) : (
                  'Allocate'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* All Email Addresses Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-violet-600" />
            <h2 className="text-lg font-semibold text-slate-800">All Email Addresses</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            All configured inbound email addresses for your organization
          </p>
        </div>

        <div className="divide-y divide-slate-200">
          {emailAddresses.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              No email addresses configured yet
            </div>
          ) : (
            emailAddresses.map((email) => {
              // Determine styling based on email type
              const getTypeStyles = (type) => {
                switch (type) {
                  case 'general':
                    return { bg: 'bg-violet-100', text: 'text-violet-600', badge: 'bg-violet-100 text-violet-700' };
                  case 'executive':
                    return { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' };
                  case 'ea_standard':
                    return { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' };
                  case 'ea_confidential':
                    return { bg: 'bg-amber-100', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' };
                  case 'trip':
                    return { bg: 'bg-green-100', text: 'text-green-600', badge: 'bg-green-100 text-green-700' };
                  default:
                    return { bg: 'bg-slate-100', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-700' };
                }
              };

              const styles = getTypeStyles(email.email_type);
              const displayType = email.email_type === 'ea_standard' ? 'Standard (Personal)'
                : email.email_type === 'ea_confidential' ? 'Confidential (Personal)'
                : email.email_type;

              return (
                <div key={email.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${styles.bg}`}>
                      {email.email_type === 'ea_confidential' ? (
                        <Lock className={`h-5 w-5 ${styles.text}`} />
                      ) : (
                        <Mail className={`h-5 w-5 ${styles.text}`} />
                      )}
                    </div>
                    <div>
                      <code className="text-sm font-mono text-slate-800">
                        {email.email_address}
                      </code>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                          {displayType}
                        </span>
                        {email.executive_name && (
                          <span className="text-xs text-slate-500">
                            {email.executive_name}
                          </span>
                        )}
                        {email.trip_title && (
                          <span className="text-xs text-slate-500">
                            {email.trip_title}
                          </span>
                        )}
                        {email.user_name && (email.email_type === 'ea_standard' || email.email_type === 'ea_confidential') && (
                          <span className="text-xs text-slate-500">
                            {email.user_name}
                          </span>
                        )}
                        {!email.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">
                      {email.total_emails_received || 0} emails
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(email.email_address, email.id)}
                    >
                      {copiedId === email.id ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
